import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, FileText, Phone, MapPin, ChevronLeft, Compass, MessageCircle, AlertTriangle } from 'lucide-react';
import articlesData from '../../data/articles.json';

const cleanNorm = (str) => {
  return String(str || '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[\u0660-\u0669]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(/[-_\s]+/g, ' ')
    .trim();
};

const isAreaMatch = (target, query) => {
  const t = cleanNorm(target);
  const q = cleanNorm(query);
  if (!t || !q) return false;
  if (t === q) return true;
  if (t === q.replace(/^ال/, '') || q === t.replace(/^ال/, '')) return true;
  if (t.replace(/\s+/g, '') === q.replace(/\s+/g, '')) return true;
  return false;
};

// Helper to format inline markdown bold and links
const formatInlineText = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #0077ff; font-weight: 700; text-decoration: underline;">$1</a>');
};

// Field Dispatch Insights based on geographic terrain & traffic reality
const getFieldDispatchInsight = (name, isHighway, isNewCity) => {
  if (isHighway) {
    return {
      typeTitle: 'طبيعة الطريق السريع والمحاور الحرة',
      badge: 'إرشاد الطوارئ على الطرق السريعة والمحاور',
      warning: `على ${name}، أخطر ما يواجه السائق هو التوقف المفاجئ في نهر الطريق بسرعات تتجاوز 90 كم/س. انحيازك الفوري لحارة الطوارئ اليمنى، وتشغيل إشارات الانتظار الرباعية (الفلاشر)، والانتظار خلف الحاجز الخرساني يحميك ويحمي سيارتك حتى وصول طاقمنا لتأمين وسحب السيارة.`,
      recommendedTruck: 'أوناش سطحة هيدروليكية مزودة بإشارات تحذيرية ليد عالية الوضوح وسلاسل شد معتمدة لتأمين السيارة في ثوانٍ معدودة وسحبها خارج مسار السرعة بأمان تام.',
      commonIssues: [
        'انفجار الإطارات أو هبوط ضغط الهواء المفاجئ نتيجة السرعات العالية وحرارة الأسفلت.',
        'غليان مياه التبريد وتلف خراطيم الردياتير تحت الأحمال المستمرة للمحرك في الصيف.',
        'فصل أو تلف دينامو الشحن وتوقف الأنظمة الكهربائية فجأة أثناء السير على سرعات عالية.'
      ]
    };
  }
  if (isNewCity) {
    return {
      typeTitle: 'طبيعة الكمبوندات والمجمعات السكنية الحديثة',
      badge: 'بروتوكول الإنقاذ للكمبوندات والجراجات الذكية',
      warning: `في ${name}، تمتاز معظم المجمعات السكنية والمولات بوجود جراجات تحت الأرض بارتفاعات محددة (أقل من 2.1 متر) وبوابات أمنية تتطلب تنسيقاً سريعاً. فريقنا يمتلك تصاريح دخول فورية وأوناشاً بارتفاعات مدمجة تدخل كافة البدارم بأمان دون عوائق.`,
      recommendedTruck: 'سطحة هيدروليكية كاملة (Full Flatbed) تنزل بزاوية صفرية ملامسة للأرض تماماً، وهي الوسيلة المعتمدة لنقل سيارات الدفع الرباعي (4x4) والسيارات الفارهة والكهربائية (EV) دون أي احتكاك للصدادات أو تلف لأنظمة التعليق الهوائي.',
      commonIssues: [
        'نوم بطارية السيارة المفاجئ في الجراجات السفلية بسبب أنظمة الحماية والتكييف.',
        'قفل ناقل الحركة الأوتوماتيكي (الفتيس) والحاجة لحوامل عجل لنقل السيارة دون جر قسري.',
        'ثقوب الإطارات الناتجة عن مخلفات التشطيب والإنشاءات في محيط المشروعات الجديدة.'
      ]
    };
  }
  // Dense urban & historical
  return {
    typeTitle: 'طبيعة الشوارع الحيوية والأحياء السكنية المزدحمة',
    badge: 'بروتوكول السحب السريع في الشوارع والأزقة الضيقة',
    warning: `في شوارع ${name} المزدحمة وتقاطعاتها الحيوية، أي توقف مفاجئ للسيارة يسبب اختناقاً مرورياً سريعاً. أولويتنا ليست مجرد رفع السيارة، بل إخلاء نهر الشارع خلال 3 إلى 5 دقائق من وصولنا لحمايتك من المشاحنات وتفادي أي احتكاكات من السيارات المارة.`,
    recommendedTruck: 'أوناش شوكة وسحب جزئي (Wheel-Lift) فائقة المناورة، مصممة خصيصاً للالتفاف والدخول في أضيق الشوارع السكنية والأسواق التجارية وسحب السيارة بمرونة وسرعة.',
    commonIssues: [
      'سخونة المحرك وتلف مراوح التبريد نتيجة التوقف المتكرر في إشارات وميادين الحي المزدحمة.',
      'تآكل أسطوانة الدبرياج أو ثقل الفرامل أثناء القيادة البطيئة في فترات الذروة.',
      'كسر المقصات أو تلف المساعدين نتيجة المطبات المرتفعة أو الحفر غير المتوقعة.'
    ]
  };
};

const AreaDedicatedArticle = ({ areaData, displayName, templateType = 'MajorDistrict', phone = '01044844492' }) => {
  const isHighway = templateType === 'Highway' || displayName.includes('طريق') || displayName.includes('محور') || displayName.includes('دائري') || displayName.includes('الأوتوستراد') || displayName.includes('صلاح سالم');
  const isNewCity = templateType === 'NewCity' || displayName.includes('مدينتي') || displayName.includes('الرحاب') || displayName.includes('زايد') || displayName.includes('أكتوبر') || displayName.includes('الشروق') || displayName.includes('بدر') || displayName.includes('التجمع');

  const dispatchInsight = useMemo(() => {
    return getFieldDispatchInsight(displayName, isHighway, isNewCity);
  }, [displayName, isHighway, isNewCity]);

  // Match the best in-depth editorial article for this area
  const matchedArticle = useMemo(() => {
    if (!displayName || !articlesData || !articlesData.articles) return null;
    const articles = articlesData.articles;
    const an = cleanNorm(displayName);

    // 1. targetArea match AND title contains area name, excluding generic FAQ
    for (const art of articles) {
      if (art.title && art.title.includes('دليل أهم الأسئلة الشائعة')) continue;
      const ta = cleanNorm(art.targetArea);
      const ti = cleanNorm(art.title);
      if (ta === an && ti.includes(an)) return art;
    }

    // 2. targetArea match, excluding generic FAQ
    for (const art of articles) {
      if (art.title && art.title.includes('دليل أهم الأسئلة الشائعة')) continue;
      const ta = cleanNorm(art.targetArea);
      if (isAreaMatch(ta, displayName)) return art;
    }

    // 3. title contains area name, excluding generic FAQ
    for (const art of articles) {
      if (art.title && art.title.includes('دليل أهم الأسئلة الشائعة')) continue;
      const ti = cleanNorm(art.title);
      if (ti.includes(an)) return art;
    }

    // 4. Any match as fallback
    for (const art of articles) {
      if (art.targetArea && isAreaMatch(art.targetArea, displayName)) return art;
    }

    return null;
  }, [displayName]);

  // Extract substantive editorial sections from the matched article content
  const editorialSections = useMemo(() => {
    if (!matchedArticle || !matchedArticle.content) return [];
    const blocks = matchedArticle.content.split('\n\n');
    const sections = [];
    let currentSec = null;

    for (const b of blocks) {
      const t = b.trim();
      if (!t || t === '---') continue;
      if (t.startsWith('## ') || t.startsWith('### ')) {
        if (currentSec && currentSec.paragraphs.length > 0) {
          sections.push(currentSec);
        }
        const heading = t.split('\n')[0].replace(/^#+\s*/, '').trim();
        const rest = t.split('\n').slice(1).join('\n').trim();
        currentSec = {
          heading,
          paragraphs: rest ? [rest] : []
        };
      } else if (currentSec) {
        if (/تواصل|اطلب|استشهد|الأسئلة الشائعة/.test(currentSec.heading)) {
          continue;
        }
        currentSec.paragraphs.push(t);
      }
    }

    if (currentSec && currentSec.paragraphs.length > 0) {
      sections.push(currentSec);
    }

    return sections.slice(0, 3);
  }, [matchedArticle]);

  const whatsappUrl = `https://wa.me/201044844492?text=${encodeURIComponent(`السلام عليكم، تعطلت سيارتي في ${displayName} وأحتاج ونش إنقاذ سريع. موقعي الجغرافي: `)}`;

  return (
    <article className="area-dedicated-article" style={{
      background: '#ffffff',
      borderRadius: '1.5rem',
      padding: '2.5rem 2rem',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '2rem',
      border: '2px solid #e3f0ff',
      direction: 'rtl',
      lineHeight: 1.9,
      color: '#1e293b'
    }}>
      {/* Article Header */}
      <header style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: '1.5rem', marginBottom: '2rem', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: '#e0f2fe',
          color: '#0369a1',
          padding: '0.4rem 1.2rem',
          borderRadius: '2rem',
          fontWeight: 700,
          fontSize: '0.95rem',
          marginBottom: '0.8rem'
        }}>
          <FileText size={18} />
          <span>الدليل الميداني الشامل للإنقاذ وسحب السيارات</span>
        </div>
        <h2 style={{
          fontSize: '1.85rem',
          fontWeight: 900,
          color: '#0a2c61',
          margin: '0.5rem 0'
        }}>
          دليل وطوارئ ونش إنقاذ السيارات في {displayName}
        </h2>
        <p style={{ color: '#64748b', fontSize: '1.05rem', margin: 0 }}>
          كل ما تحتاج معرفته عند تعطل سيارتك أو طلب ونش سريع في {displayName} والمناطق المحيطة
        </p>
      </header>

      {/* Featured Editorial Summary */}
      {matchedArticle && matchedArticle.summary && (
        <section style={{
          background: '#f0f9ff',
          borderRight: '4px solid #0284c7',
          padding: '1.2rem 1.5rem',
          borderRadius: '0.8rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0369a1', fontWeight: 800, fontSize: '1.1rem', marginBottom: '0.4rem' }}>
            <Compass size={20} />
            <span>نظرة عامة على خدمة الإنقاذ في {displayName}</span>
          </div>
          <p style={{ margin: 0, fontSize: '1.05rem', color: '#0c4a6e', lineHeight: 1.8 }}>
            {matchedArticle.summary}
          </p>
        </section>
      )}

      {/* Section 1: Local Driving & Traffic Context */}
      <section style={{ marginBottom: '2.2rem' }}>
        <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0077ff', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MapPin size={22} color="#0077ff" />
          طبيعة الحركة المرورية ونقاط التمركز في {displayName}
        </h3>
        
        {areaData && areaData.intro ? (
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#334155' }}>
            {areaData.intro}
          </p>
        ) : (
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#334155' }}>
            تتميز منطقة <strong>{displayName}</strong> بحركة مرورية حيوية ومستمرة على مدار الـ 24 ساعة، حيث تربط بين شبكة من الشرايين الرئيسية والمحاور الحيوية. هذا التدفق المروري يستوجب تدخلاً فورياً وسريعاً من فرق الإنقاذ لتفادي تعطل حركة السير أو تعريض السيارة للخطر.
          </p>
        )}

        {/* Local Streets & Landmarks Badges */}
        {areaData && ((areaData.streets && areaData.streets.length > 0) || (areaData.landmarks && areaData.landmarks.length > 0)) && (
          <div style={{ background: '#f8fafc', padding: '1.2rem', borderRadius: '1rem', border: '1px solid #e2e8f0', marginTop: '1rem' }}>
            {areaData.streets && areaData.streets.length > 0 && (
              <div style={{ marginBottom: areaData.landmarks && areaData.landmarks.length > 0 ? '0.8rem' : 0 }}>
                <strong style={{ color: '#0a2c61', display: 'block', marginBottom: '0.4rem' }}>
                  أبرز المحاور والشوارع المغطاة في {displayName}:
                </strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {areaData.streets.slice(0, 8).map((st, i) => (
                    <span key={i} style={{ background: '#e2e8f0', color: '#1e293b', padding: '0.2rem 0.7rem', borderRadius: '0.5rem', fontSize: '0.92rem', fontWeight: 600 }}>
                      {st}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {areaData.landmarks && areaData.landmarks.length > 0 && (
              <div>
                <strong style={{ color: '#0a2c61', display: 'block', marginBottom: '0.4rem' }}>
                  أهم المعالم ونقاط التمركز الحيوية:
                </strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {areaData.landmarks.slice(0, 6).map((lm, i) => (
                    <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '0.2rem 0.7rem', borderRadius: '0.5rem', fontSize: '0.92rem', fontWeight: 600 }}>
                      {lm}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Section 2: Authentic Master Field Dispatch Insight */}
      <section style={{
        background: '#fefce8',
        border: '1px solid #fef08a',
        borderRight: '5px solid #eab308',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '2.2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#854d0e', fontWeight: 800, fontSize: '1.15rem', marginBottom: '0.5rem' }}>
          <AlertTriangle size={22} color="#ca8a04" />
          <span>ملاحظة الأسطى الميدانية ({dispatchInsight.badge})</span>
        </div>
        <p style={{ margin: '0 0 1rem 0', fontSize: '1.05rem', color: '#713f12', lineHeight: 1.8 }}>
          {dispatchInsight.warning}
        </p>

        <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '0.8rem', border: '1px solid #fde047', marginBottom: '1rem' }}>
          <strong style={{ color: '#0a2c61', display: 'block', marginBottom: '0.3rem' }}>
            🚛 نوع الونش الموصى به لـ {displayName}:
          </strong>
          <p style={{ margin: 0, fontSize: '0.98rem', color: '#334155', lineHeight: 1.7 }}>
            {dispatchInsight.recommendedTruck}
          </p>
        </div>

        <strong style={{ color: '#854d0e', display: 'block', marginBottom: '0.4rem' }}>
          أكثر بلاغات الأعطال التي نتعامل معها يومياً في هذا النطاق:
        </strong>
        <ul style={{ margin: 0, paddingRight: '1.5rem', color: '#713f12', lineHeight: 1.8 }}>
          {dispatchInsight.commonIssues.map((issue, idx) => (
            <li key={idx} style={{ marginBottom: '0.3rem' }}>{issue}</li>
          ))}
        </ul>
      </section>

      {/* Section 3: Unique Editorial Excerpts from Matched Article */}
      {editorialSections && editorialSections.length > 0 && (
        <section style={{ marginBottom: '2.2rem' }}>
          {editorialSections.map((sec, sIdx) => (
            <div key={sIdx} style={{ marginBottom: '1.8rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0077ff', marginBottom: '0.6rem' }}>
                {sec.heading}
              </h3>
              {sec.paragraphs.map((p, pIdx) => {
                const trimmed = p.trim();
                if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                  const items = trimmed.split('\n').filter((it) => it.trim());
                  return (
                    <ul key={pIdx} style={{ paddingRight: '1.5rem', margin: '0.5rem 0 1rem 0', lineHeight: 1.8 }}>
                      {items.map((item, itIdx) => (
                        <li key={itIdx} dangerouslySetInnerHTML={{ __html: formatInlineText(item.replace(/^[-*]\s*/, '')) }} />
                      ))}
                    </ul>
                  );
                }
                return (
                  <p key={pIdx} style={{ fontSize: '1.05rem', color: '#334155', marginBottom: '0.8rem', lineHeight: 1.85 }} dangerouslySetInnerHTML={{ __html: formatInlineText(trimmed) }} />
                );
              })}
            </div>
          ))}
        </section>
      )}

      {/* Section 4: Transparent Egyptian Pricing Guide */}
      <section style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #cbd5e1', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0a2c61', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ShieldCheck size={22} color="#16a34a" />
          شفافية الأسعار وضمانات الخدمة مع ونش انقاذ محمد بحر
        </h3>
        <p style={{ margin: '0 0 1rem 0', fontSize: '1.05rem', color: '#334155' }}>
          نلتزم بتسعيرة واضحة يتم الاتفاق عليها هاتفياً قبل تحرك الونش شاملة النقل والتأمين، بدون أي رسوم خفية أو إكراميات مفروضة في الشارع:
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.8rem', marginBottom: '1.2rem' }}>
          <div style={{ background: '#ffffff', padding: '0.8rem 1rem', borderRadius: '0.6rem', border: '1px solid #e2e8f0' }}>
            <strong style={{ color: '#0077ff', display: 'block', fontSize: '0.95rem' }}>سحب ملاكي داخل {displayName}</strong>
            <span style={{ color: '#0f172a', fontWeight: 700 }}>يبدأ من 250 ج.م</span>
          </div>
          <div style={{ background: '#ffffff', padding: '0.8rem 1rem', borderRadius: '0.6rem', border: '1px solid #e2e8f0' }}>
            <strong style={{ color: '#0077ff', display: 'block', fontSize: '0.95rem' }}>سحب SUV وسيارات الدفع الرباعي</strong>
            <span style={{ color: '#0f172a', fontWeight: 700 }}>يبدأ من 350 ج.م</span>
          </div>
          <div style={{ background: '#ffffff', padding: '0.8rem 1rem', borderRadius: '0.6rem', border: '1px solid #e2e8f0' }}>
            <strong style={{ color: '#0077ff', display: 'block', fontSize: '0.95rem' }}>إسعاف طريق (بطارية / بنزين / إطار)</strong>
            <span style={{ color: '#0f172a', fontWeight: 700 }}>يبدأ من 180 ج.م</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0f172a', fontWeight: 600 }}>
            <CheckCircle2 size={18} color="#16a34a" />
            <span>خصم فوري 50% على الخدمات الأساسية</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0f172a', fontWeight: 600 }}>
            <CheckCircle2 size={18} color="#16a34a" />
            <span>تسعيرة ثابتة معلنة قبل انطلاق الونش</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0f172a', fontWeight: 600 }}>
            <CheckCircle2 size={18} color="#16a34a" />
            <span>بدون أي رسوم خفية أو إكراميات</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0f172a', fontWeight: 600 }}>
            <CheckCircle2 size={18} color="#16a34a" />
            <span>تأمين شامل على سلامة السيارة أثناء السحب</span>
          </div>
        </div>

        {/* Dual Emergency Call & WhatsApp Dispatch Box */}
        <div style={{
          marginTop: '1.5rem',
          background: 'linear-gradient(135deg, #0a2c61 0%, #1976d2 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.2rem', marginBottom: '0.2rem' }}>هل تعطلت سيارتك في {displayName} الآن؟</div>
            <div style={{ opacity: 0.9, fontSize: '0.95rem' }}>أقرب ونش متمركز في موقعك جاهز للتحرك فوراً (استجابة خلال {areaData && areaData.responseTime ? areaData.responseTime : '10-15 دقيقة'})</div>
          </div>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
            <a href={`tel:${phone}`} style={{
              background: '#ffc107',
              color: '#0a2c61',
              padding: '0.7rem 1.6rem',
              borderRadius: '2rem',
              fontWeight: 900,
              textDecoration: 'none',
              fontSize: '1.05rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Phone size={18} /> اتصل: {phone}
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={{
              background: '#25D366',
              color: '#ffffff',
              padding: '0.7rem 1.6rem',
              borderRadius: '2rem',
              fontWeight: 800,
              textDecoration: 'none',
              fontSize: '1.05rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <MessageCircle size={18} /> إرسال اللوكيشن
            </a>
          </div>
        </div>

        {/* Deep Link to the Standalone Dedicated Article Guide */}
        {matchedArticle && matchedArticle.slug && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem 1.2rem',
            background: '#ffffff',
            border: '1px dashed #0077ff',
            borderRadius: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.8rem'
          }}>
            <div style={{ fontSize: '0.98rem', color: '#334155' }}>
              💡 للمزيد من التفاصيل الفنية وتحليلات المرور والأعطال:
            </div>
            <Link
              to={`/articles/${matchedArticle.slug}`}
              style={{
                color: '#0077ff',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.95rem'
              }}
            >
              <span>قراءة الدليل التخصصي لـ {displayName} في المدونة</span>
              <ChevronLeft size={16} />
            </Link>
          </div>
        )}
      </section>
    </article>
  );
};

export default AreaDedicatedArticle;
